<!-- eslint-disable vue/attributes-order -->
<template>
  <div class="tb-button-group !border-l-0">
    <SectionItem
      v-bind="customProps"
      v-for="{
        effect,
        icon,
        tooltip,
        tooltipMeta,
        disabled,
        active,
        customProps,
        customButton,
        label,
        key,
      } in commands"
      :key="key ?? icon"
      :effect="effect"
      :icon="icon"
      :tooltip="tooltip"
      :meta="tooltipMeta"
      :label="label"
      :active="!!active?.()"
      :disabled="!!disabled?.()"
    >
      <component
        v-bind="{
          icon,
          effect,
          active: !!active?.(),
          disabled: !!disabled?.(),
          editor,
          ...customProps,
        }"
        :is="customButton"
      />
    </SectionItem>
  </div>
</template>

<script setup lang="ts">
import { Editor } from '@tiptap/core';
import SectionItem from '@/components/editor/toolbar/SectionItem.vue';
import useTranslate from '@/utilities/composables/useTranslate';
import type { MenuItemFn, MenuItem, MenuItemOptions } from '@/components/editor/toolbar/menu-item';
import { computed, getCurrentInstance, provide, readonly, ref } from 'vue';

const props = defineProps<{
  editor: Editor;
  commands: MenuItemFn;
  options?: MenuItemOptions;
}>();

const { translate } = useTranslate();

const _this = getCurrentInstance();

/**
 * When this value is set to anything other than null,
 * these commands will populate the menu.
 */
const commandOverrides = ref<MenuItemFn | null>(null);

/**
 * Calling this changes the individual menu section contents.
 * Useful for creating "sub-menus" within the same toolbar.
 * @param com The menu item function, which returns the
 * required collection.
 */
function overrideCommands(com: MenuItemFn) {
  commandOverrides.value = com;
}

/**
 * Revert the menu section to its initial state, which
 * is using the props given to the MenuSection.
 */
function revertCommandOverrides() {
  commandOverrides.value = null;
}

provide('sectionCommandOverride', {
  commandOverrides: readonly(commandOverrides),
  overrideCommands,
  revertCommandOverrides,
});

const commandSource = computed(() => commandOverrides.value ?? props.commands);

const commands = computed(() =>
  commandSource
    .value({
      editor: props.editor,
      translate,
      store: _this?.proxy.$store!,
      appendTo: props?.options?.aiPopupAppendTo,
    })
    .filter((command: MenuItem) => {
      if (!command.hidden) return true;
      return command.hidden();
    })
);

provide('editor', props.editor);
</script>
